import type { ApolloClient, ApolloQueryResult, NormalizedCacheObject } from '@apollo/client';
import type { SagaIterator } from 'redux-saga';
import { call, delay, getContext, put, select, takeLeading } from 'redux-saga/effects';
import storage from 'store2';

import { UNAUTHORIZED } from '@skippay/core/constants/httpStatusCodes';
import { getSignedInUser, loginWithToken } from '@skippay/core/firebase/auth';
import {
	BreadcrumbCategory,
	captureBreadcrumb,
	captureException,
} from '@skippay/core/global/errorTracking';
import type { History } from '@skippay/core/hooks/useLocation';
import {
	FIREBASE_LOGIN_REQUESTED,
	clearUserData,
	requestFirebaseLogin,
} from '@skippay/core/modules/user';
import { NetworkError } from '@skippay/core/utils/GqlEngine';
import { getErrorCode, getOriginalError } from '@skippay/core/utils/gqlUtils';

import AccountFirebaseTokenQuery from 'queries/AccountFirebaseTokenQuery.graphql';
import { isPaymentGatePathname } from 'utils/routeUtils';

import { isLoggedInSelector } from '../selectors';

// https://firebase.google.com/docs/reference/js/auth#autherrorcodes
const TRANSIENT_FIREBASE_ERRORS = ['auth/network-request-failed', 'auth/timeout'];
const ABORTED_REQUEST_CODES = ['ECONNABORTED', 'ERR_CANCELED'];
const MAX_ATTEMPTS = 3;
const BASE_DELAY_MS = 1000;

interface AccountFirebaseTokenQueryData {
	accountFirebaseToken: { customToken?: string | null } | null;
}

interface LoginFailure {
	code?: string;
	message?: string;
	networkError?: Error | null;
}

const getNetworkErrorCode = (failure: LoginFailure): string | undefined =>
	failure.networkError instanceof NetworkError ? failure.networkError.extensions?.code : undefined;

const isTransientFailure = (failure: LoginFailure): boolean =>
	failure.networkError instanceof NetworkError ||
	TRANSIENT_FIREBASE_ERRORS.includes(failure.code ?? '');

const isSessionExpired = (failure: LoginFailure): boolean =>
	getErrorCode(getOriginalError(failure)) === UNAUTHORIZED;

const isExpectedOutage = (failure: LoginFailure): boolean =>
	navigator.onLine === false || ABORTED_REQUEST_CODES.includes(getNetworkErrorCode(failure) ?? '');

function reportFailure(failure: LoginFailure, attempt: number): void {
	if (failure.code === 'auth/invalid-custom-token') {
		return;
	}

	captureException(new Error('Firebase login error'), {
		extra: { originalError: getOriginalError(failure), code: failure.code, attempt },
	});
}

function reportExhaustedRetries(failure: LoginFailure, attempt: number): void {
	const code = failure.code ?? getNetworkErrorCode(failure);

	if (isExpectedOutage(failure)) {
		captureBreadcrumb(
			BreadcrumbCategory.firebaseDatabase,
			'Firebase login skipped while offline',
			{ code },
			'warning'
		);

		return;
	}

	captureException(new Error('Firebase login unavailable'), {
		level: 'warning',
		extra: { code, message: failure.message, attempt },
	});
}

function* getCustomToken(): SagaIterator<string> {
	const apolloClient: ApolloClient<NormalizedCacheObject> = yield getContext('apolloClient');
	const { data }: ApolloQueryResult<AccountFirebaseTokenQueryData> = yield call(
		apolloClient.query,
		{
			fetchPolicy: 'network-only',
			query: AccountFirebaseTokenQuery,
		}
	);
	const customToken = data.accountFirebaseToken?.customToken;

	if (!customToken) {
		throw new Error('No custom token');
	}

	return customToken;
}

export function* firebaseLoginTask(): SagaIterator<void> {
	if (process.env.NODE_ENV === 'development' && !process.env.NO_MOCK && !storage.get('NO_MOCK')) {
		return;
	}

	const signedInUser = yield call(getSignedInUser);

	if (signedInUser) {
		return;
	}

	yield put(clearUserData());

	for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
		try {
			const customToken = yield call(getCustomToken);
			yield call(loginWithToken, customToken);

			return;
		} catch (error) {
			const failure = error as LoginFailure;

			if (isSessionExpired(failure)) {
				return;
			}

			if (!isTransientFailure(failure)) {
				reportFailure(failure, attempt);

				return;
			}

			if (attempt === MAX_ATTEMPTS) {
				reportExhaustedRetries(failure, attempt);

				return;
			}

			yield delay(BASE_DELAY_MS * 2 ** (attempt - 1));
		}
	}
}

export default function* watchFirebaseLogin(): SagaIterator<void> {
	yield takeLeading(FIREBASE_LOGIN_REQUESTED, firebaseLoginTask);

	const isLoggedIn: boolean = yield select(isLoggedInSelector);

	if (!isLoggedIn) {
		return;
	}

	const history: History = yield getContext('history');

	if (!isPaymentGatePathname(history.location.pathname)) {
		yield put(requestFirebaseLogin());
	}
}
